import { env } from '@/env/client';
import { InMemoryAudioSegmentStore } from '@/lib/recording/audio-segment-store';
import { encodePcm16ToWav } from '@/lib/recording/wav';
import type { TranscriptionToken } from '@/lib/transcription/types';
import { useConvexAction, useConvexMutation } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

const AUDIO_SAMPLE_RATE_HZ = 44100;
const AUDIO_CHANNELS = 1;
const AUDIO_CODEC = 'pcm_s16le';
const TOKEN_FORMAT_VERSION = 1;

export { AUDIO_CHANNELS, AUDIO_CODEC, AUDIO_SAMPLE_RATE_HZ };

type ActiveSegment = {
  seq: number;
  startMs: number;
};

/**
 * In-memory segment capture for now.
 * The storage interface is isolated so a native temp-file implementation can
 * replace this without changing the transcription flow.
 */
export const useAudioCapture = ({ reportId }: { reportId: Id<'reports'> }) => {
  const storeRef = useRef(new InMemoryAudioSegmentStore());
  const activeSegmentRef = useRef<ActiveSegment | null>(null);

  const { mutateAsync: commitSegmentMutation } = useMutation({
    mutationFn: useConvexMutation(api.recording.commitSegment),
  });

  const { mutateAsync: getSegmentUploadUrls } = useMutation({
    mutationFn: useConvexAction(api.recording.getSegmentUploadUrls),
  });

  const init = useCallback((baseOffsetMs: number, seq: number) => {
    activeSegmentRef.current = { seq, startMs: baseOffsetMs };
    storeRef.current.clear();
  }, []);

  const addPayload = useCallback((payload: ArrayBuffer) => {
    storeRef.current.appendPayload(payload);
  }, []);

  const hasCapturedAudio = useCallback(() => {
    return storeRef.current.getByteLength() > 0;
  }, []);

  const flush = useCallback(async () => {
    // No-op for in-memory capture. Keep async shape for future file-backed sinks.
    await Promise.resolve();
  }, []);

  const commitSegment = useCallback(
    async (
      consumeFinalTokensForRange: (
        startMs: number,
        endMs: number,
      ) => TranscriptionToken[],
    ) => {
      const activeSegment = activeSegmentRef.current;
      if (!activeSegment) {
        throw new Error(
          'Cannot commit segment without active segment metadata.',
        );
      }

      const pcmData = storeRef.current.readAllPcm();
      if (pcmData.byteLength === 0) {
        throw new Error('No captured audio to commit.');
      }

      const bytesPerSampleFrame = 2 * AUDIO_CHANNELS;
      if (pcmData.byteLength % bytesPerSampleFrame !== 0) {
        throw new Error('Captured PCM payload is misaligned.');
      }

      const sampleFrames = pcmData.byteLength / bytesPerSampleFrame;
      const durationMs = Math.max(
        1,
        Math.round((sampleFrames / AUDIO_SAMPLE_RATE_HZ) * 1000),
      );
      const endMs = activeSegment.startMs + durationMs;

      const wavData = encodePcm16ToWav(
        pcmData,
        AUDIO_SAMPLE_RATE_HZ,
        AUDIO_CHANNELS,
      );
      const uploadData = new Uint8Array(wavData.byteLength);
      uploadData.set(wavData);

      const { audioUploadUrl, tokensUploadUrl } = await getSegmentUploadUrls({
        reportId,
        seq: activeSegment.seq,
        source: 'live',
      });

      const audioUploadResponse = await fetch(audioUploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'audio/wav' },
        body: new Blob([uploadData], { type: 'audio/wav' }),
      });

      if (!audioUploadResponse.ok) {
        throw new Error(
          `Segment audio upload failed: ${audioUploadResponse.status}`,
        );
      }

      const absoluteTokens = consumeFinalTokensForRange(
        activeSegment.startMs,
        Number.MAX_SAFE_INTEGER,
      );

      const relativeTokens: TranscriptionToken[] = absoluteTokens.map(
        (token) => ({
          ...token,
          startMs: Math.max(0, token.startMs - activeSegment.startMs),
          endMs: Math.max(0, token.endMs - activeSegment.startMs),
        }),
      );

      const tokenPayload = {
        version: TOKEN_FORMAT_VERSION,
        provider: 'soniox',
        model: env.VITE_SONIOX_RT_MODEL,
        createdAt: Date.now(),
        tokens: relativeTokens,
      };

      const tokenUploadResponse = await fetch(tokensUploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: new Blob([JSON.stringify(tokenPayload)], {
          type: 'application/json',
        }),
      });

      if (!tokenUploadResponse.ok) {
        throw new Error(
          `Segment token upload failed: ${tokenUploadResponse.status}`,
        );
      }

      await commitSegmentMutation({
        reportId,
        seq: activeSegment.seq,
        startMs: activeSegment.startMs,
        endMs,
        durationMs,
        sampleRateHz: AUDIO_SAMPLE_RATE_HZ,
        channels: AUDIO_CHANNELS,
        codec: AUDIO_CODEC,
        tokenCount: relativeTokens.length,
        audioSizeBytes: wavData.byteLength,
      });
    },
    [commitSegmentMutation, getSegmentUploadUrls, reportId],
  );

  const cleanup = useCallback(() => {
    activeSegmentRef.current = null;
    storeRef.current.clear();
  }, []);

  return {
    init,
    addPayload,
    hasCapturedAudio,
    flush,
    commitSegment,
    cleanup,
  };
};
